import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Cross2Icon } from "@radix-ui/react-icons";
import confettiLeftIllustration from "../../../static/illustration--confetti-left.svg";
import confettiRightIllustration from "../../../static/illustration--confetti-right.svg";
import { PieChart, Pie, Cell } from "recharts";
import useRecommendedPortfolio from "../../../hooks/useRecommendedPortfolio";
import PortfolioInformation from "../PortfolioInformation";
import Loading from "../Loading";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";

export default function HasPortfolioOnTrack({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  screen,
}) {
  const { user } = useAppStore();
  const { setAnswer } = useAnswer(screen.id);
  const [selectedValue, setSelectedValue] = useState("noChanges");
  const [probabilityText, setProbabilityText] = useState("");
  const [profileDescription, setProfileDescription] = useState("");

  const { recommendedPortfolio, recommendedPortfolioInfo, loading } =
    useRecommendedPortfolio({});

  useEffect(() => {
    if (!recommendedPortfolioInfo) {
      return;
    }

    // Dynamically determine if portfolio is defensive or offensive
    // based on the recommended portfolio from the API
    const portfolioName = recommendedPortfolioInfo.name || "";
    const isDefensive = portfolioName.toLowerCase().includes("defensive");

    // Set answer based on portfolio type
    updateAnswer(isDefensive ? "defensive" : "offensive");
    setProfileDescription(recommendedPortfolioInfo.description || "");

    console.log("Screen 2.9 - Portfolio routing:", {
      portfolioName,
      isDefensive,
      answer: isDefensive ? "defensive" : "offensive",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedPortfolioInfo]);

  useEffect(() => {
    // Update the probability text based on the selected value
    let text = "";
    if (selectedValue === "noChanges") {
      text = "";
    } else if (selectedValue === "raiseYourContributions") {
      text = " if you raise your contributions";
    } else if (selectedValue === "workLonger") {
      text = " if you work longer";
    } else if (selectedValue === "lowerYourGoal") {
      text = " if you lower your goal";
    }
    setProbabilityText(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  function updateAnswer(value) {
    setAnswer(value);
    updateValidation(value.length > 0);
  }

  const baseProbability = Math.round(
    recommendedPortfolio?.recommendedPortfolioProbability,
  );

  const initialPieData = [
    { name: "Probability Achieved", value: baseProbability },
    { name: "Probability Remaining", value: 100 - baseProbability },
  ];

  // State for storing the updated pieData
  const [updatedPieData, setUpdatedPieData] = useState(initialPieData);

  // useEffect hook to update pieData based on selectedValue and API response
  useEffect(() => {
    // Update initial data when recommendedPortfolio changes
    if (!recommendedPortfolio) {
      return;
    }

    const apiProbability = Math.round(
      recommendedPortfolio.recommendedPortfolioProbability || 0,
    );

    // Define the new values for the pieData based on selectedValue
    let probability = apiProbability;

    if (selectedValue === "raiseYourContributions") {
      // Use probabilityPlus100 from API, fallback to +10%
      probability =
        recommendedPortfolio.probabilityPlus100 !== null
          ? Math.round(recommendedPortfolio.probabilityPlus100)
          : apiProbability + 10;
    } else if (selectedValue === "workLonger") {
      // Use probabilityFiveMoreYears from API, fallback to +20%
      probability =
        recommendedPortfolio.probabilityFiveMoreYears !== null
          ? Math.round(recommendedPortfolio.probabilityFiveMoreYears)
          : apiProbability + 20;
    } else if (selectedValue === "lowerYourGoal") {
      // Use probabilityTenPercentDecrease from API, fallback to -10%
      probability =
        recommendedPortfolio.probabilityTenPercentDecrease !== null
          ? Math.round(recommendedPortfolio.probabilityTenPercentDecrease)
          : apiProbability - 10;
    }

    setUpdatedPieData([
      { name: "Probability Achieved", value: probability },
      { name: "Probability Remaining", value: 100 - probability },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue, recommendedPortfolio]);

  const colors = ["#80E49F", "#D9F8E2"];

  return loading || !recommendedPortfolio ? (
    <Loading />
  ) : (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <section className="w-full h-full px-6 py-16 text-center bg-white md:px-10 md:py-10 lg:px-16 lg:py-20">
        <div className="flex flex-col md:h-full">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center mb-4 md:mb-5">
              <img
                className="block w-12 h-auto md:w-16"
                src={confettiLeftIllustration}
                alt=""
              />
              <h1 className="mx-3 text-3xl md:mx-5 md:text-4xl">
                Your results are in!
              </h1>
              <img
                className="block w-10 h-auto md:w-14"
                src={confettiRightIllustration}
                alt=""
              />
            </div>
            <h2 className="text-base font-normal !leading-tight md:text-lg">
              The portfolio that will best help you reach your goal is:
              <br />
              <strong>
                {recommendedPortfolioInfo?.name ||
                  user.artesysPortfolioDescription}
              </strong>
              <Popover.Root>
                <Popover.Trigger asChild>
                  <button className="relative ml-[.25em] shadow-none before:absolute before:left-1/2 before:top-1/2 before:block before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']">
                    <FontAwesomeIcon
                      icon="fa-regular fa-circle-info"
                      className="text-[.944em] text-as-green-100"
                    />
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    sideOffset={5}
                    className="z-50 w-64 rounded bg-white p-5 shadow-xl will-change-[transform,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-as-green-50 data-[side=bottom]:animate-slide-up-fade data-[side=left]:animate-slide-right-fade data-[side=right]:animate-slide-left-fade data-[side=top]:animate-slide-down-fade"
                  >
                    <div className="text-xs">
                      <p>{profileDescription}</p>
                    </div>
                    <Popover.Close
                      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full font-[inherit] text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-900"
                      aria-label="Close"
                    >
                      <Cross2Icon />
                    </Popover.Close>
                    <Popover.Arrow className="fill-white" />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </h2>
          </div>
          <div className="mx-auto max-w-[45rem]">
            <div className="px-6 py-6 border rounded-md border-as-green-100 md:mt-12 md:px-12 md:py-8">
              <h3 className="text-base font-bold">
                Probability of hitting your goal
                <span className="text-as-green-100">{probabilityText}</span>:
              </h3>
              <div className="grid grid-cols-1 gap-8 mt-6 md:mt-8 md:grid-cols-2 md:gap-16">
                <div className="justify-self-center md:justify-self-end">
                  <div className="relative h-[11.625rem] w-[11.625rem]">
                    <PieChart width={186} height={186}>
                      <Pie
                        data={updatedPieData}
                        cx={88}
                        cy={88}
                        labelLine={false}
                        innerRadius={68}
                        outerRadius={93}
                        fill="none"
                        stroke="none"
                        dataKey="value"
                        startAngle={-270}
                      >
                        {updatedPieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[index % colors.length]}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[3.125rem] font-extralight leading-tight text-as-green-100">
                        {updatedPieData[0].value}
                        <sup className="">%</sup>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="justify-self-center md:self-center md:justify-self-start">
                  <ul className="space-y-2 text-sm text-left text-gray-800">
                    <PortfolioInformation
                      recommendedPortfolio={recommendedPortfolio}
                      accountValue={user.accountValue}
                      adjustment={selectedValue}
                    />
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-8 md:mt-12">
              <fieldset>
                <legend className="mb-3 text-base text-left text-gray-600">
                  Toggle ways to improve your outcome:
                </legend>
                <div className="space-y-4">
                  <div>
                    <RadioGroup.Root
                      aria-label="Ways to improve your outcome"
                      value={selectedValue}
                      onValueChange={setSelectedValue}
                      defaultValue="noChanges"
                    >
                      <div className="btn-group btn-group--secondary">
                        <label
                          className="btn-group__label"
                          htmlFor="toggleOutcomeNoChanges"
                        >
                          <RadioGroup.Item
                            value="noChanges"
                            id="toggleOutcomeNoChanges"
                          >
                            <RadioGroup.Indicator />
                            No Changes
                          </RadioGroup.Item>
                        </label>
                        <label
                          className="btn-group__label"
                          htmlFor="toggleOutcomeRaiseContributions"
                        >
                          <RadioGroup.Item
                            value="raiseYourContributions"
                            id="toggleOutcomeRaiseContributions"
                          >
                            <RadioGroup.Indicator />
                            <span>Raise Your Contributions</span>
                            <span className="btn-group__subtitle">
                              +$100/month
                            </span>
                          </RadioGroup.Item>
                        </label>
                        <label
                          className="btn-group__label"
                          htmlFor="toggleOutcomeWorkLonger"
                        >
                          <RadioGroup.Item
                            value="workLonger"
                            id="toggleOutcomeWorkLonger"
                          >
                            <RadioGroup.Indicator />
                            <span>Work Longer</span>
                            <span className="btn-group__subtitle">
                              +5 years
                            </span>
                          </RadioGroup.Item>
                        </label>
                        <label
                          className="btn-group__label"
                          htmlFor="toggleOutcomeLowerYourGoal"
                        >
                          <RadioGroup.Item
                            value="lowerYourGoal"
                            id="toggleOutcomeLowerYourGoal"
                          >
                            <RadioGroup.Indicator />
                            <span>Lower Your Goal</span>
                            <span className="btn-group__subtitle">-10%</span>
                          </RadioGroup.Item>
                        </label>
                      </div>
                    </RadioGroup.Root>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div className="flex justify-center gap-3 mt-12 md:mt-16 md:gap-5">
            <button className="btn btn--secondary" onClick={previousScreen}>
              Back
            </button>
            <button
              className="btn btn--primary"
              disabled={Boolean(validation) === false}
              onClick={advanceScreen}
            >
              Continue
            </button>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
